import * as React from 'react';
import { useState, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { Data, Layout } from 'plotly.js';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import InputLabel from '@mui/material/InputLabel';
import { plotlyLayout, plotlyConfig, lineTraceTemplate } from '../theme/plotlyTheme';
import { colors } from '../theme/colors';
import { PlotControlsToolbar } from './PlotControlsToolbar';
import { PlotFullscreenModal } from './PlotFullscreenModal';

interface PlotData {
  measured_value: string;
  timestamps: string[][];
  values: number[][];
  point_names: string[];
  shown_names: string[];
}

interface PlotDataProps {
  plot_data: PlotData;
}



const movingAverage = (data: number[], smoothness: number): number[] => {
  if (smoothness <= 1) return data;
  const smoothed = [];
  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - Math.floor(smoothness / 2));
    const end = Math.min(data.length, i + Math.ceil(smoothness / 2));
    const subset = data.slice(start, end);
    const average = subset.reduce((a, b) => a + b, 0) / subset.length;
    smoothed.push(average);
  }
  return smoothed;
};

const gaussianSmoothing = (data: number[], smoothness: number): number[] => {
  if (smoothness <= 1) return data;

  const kernel = createGaussianKernel(smoothness);
  const half = Math.floor(kernel.length / 2);
  const smoothed = [];

  for (let i = 0; i < data.length; i++) {
    let sum = 0;
    let weightSum = 0;
    for (let j = 0; j < kernel.length; j++) {
      const index = i + j - half;
      if (index >= 0 && index < data.length) {
        sum += data[index] * kernel[j];
        weightSum += kernel[j];
      }
    }
    smoothed.push(sum / weightSum);
  }
  return smoothed;
};

const createGaussianKernel = (size: number): number[] => {
  const sigma = size / 6; // Standard deviation
  const kernel = [];
  let sum = 0;
  for (let i = 0; i < size; i++) {
    const x = i - Math.floor(size / 2);
    const value = Math.exp(-0.5 * (x * x) / (sigma * sigma));
    kernel.push(value);
    sum += value;
  }
  return kernel.map(v => v / sum);
};

export const MetricsPlot: React.FC<PlotDataProps> = ({ plot_data }) => {
  const [smoothness, setSmoothness] = useState(1);
  const [smoothingMethod, setSmoothingMethod] = useState('None');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [gridVisible, setGridVisible] = useState(true);
  const [legendVisible, setLegendVisible] = useState(true);
  const [layout, setLayout] = useState<Partial<Layout>>({
    ...plotlyLayout,
    xaxis: { ...plotlyLayout.xaxis, title: 'Время' },
    yaxis: { ...plotlyLayout.yaxis, title: plot_data.measured_value },
  });

  const data_for_plot: Data[] = [];

  for (let i = 0; i < plot_data.point_names.length; i++) {
    const timestamps = plot_data.timestamps[i].map(item => new Date(item));

    let smoothedY = plot_data.values[i];
    if (smoothingMethod === 'Moving Average') {
      smoothedY = movingAverage(plot_data.values[i], smoothness);
    } else if (smoothingMethod === 'Gaussian') {
      smoothedY = gaussianSmoothing(plot_data.values[i], smoothness);
    }

    data_for_plot.push({
      ...lineTraceTemplate,
      x: timestamps,
      y: smoothedY,
      name: plot_data.shown_names[i],
    });
  }


  const handleRelayout = useCallback((newLayout: any) => {
    setLayout(prevLayout => {
      const updates: any = { ...prevLayout };

      // Handle x-axis range changes
      if ('xaxis.range[0]' in newLayout && 'xaxis.range[1]' in newLayout) {
        updates.xaxis = {
          ...prevLayout.xaxis,
          range: [newLayout['xaxis.range[0]'], newLayout['xaxis.range[1]']],
          autorange: false,
        };
      } else if ('xaxis.range' in newLayout) {
        updates.xaxis = {
          ...prevLayout.xaxis,
          range: newLayout['xaxis.range'],
          autorange: false,
        };
      } else if ('xaxis.autorange' in newLayout) {
        updates.xaxis = {
          ...prevLayout.xaxis,
          autorange: newLayout['xaxis.autorange'],
          range: undefined,
        };
      }

      // Handle y-axis range changes
      if ('yaxis.range[0]' in newLayout && 'yaxis.range[1]' in newLayout) {
        updates.yaxis = {
          ...prevLayout.yaxis,
          range: [newLayout['yaxis.range[0]'], newLayout['yaxis.range[1]']],
          autorange: false,
        };
      } else if ('yaxis.range' in newLayout) {
        updates.yaxis = {
          ...prevLayout.yaxis,
          range: newLayout['yaxis.range'],
          autorange: false,
        };
      } else if ('yaxis.autorange' in newLayout) {
        updates.yaxis = {
          ...prevLayout.yaxis,
          autorange: newLayout['yaxis.autorange'],
          range: undefined,
        };
      }

      return updates;
    });
  }, []);

  const handleFullscreen = () => {
    setIsFullscreen(true);
  };

  const handleResetZoom = () => {
    setLayout(prevLayout => ({
      ...prevLayout,
      xaxis: {
        ...prevLayout.xaxis,
        autorange: true,
        range: undefined,
      },
      yaxis: {
        ...prevLayout.yaxis,
        autorange: true,
        range: undefined,
      },
    }));
  };

  const handleToggleGrid = () => {
    setGridVisible(prev => !prev);
  };

  const handleToggleLegend = () => {
    setLegendVisible(prev => !prev);
  };

  const handleCopyData = () => {
    // Convert plot data to CSV string
    const headers = ['timestamp', ...plot_data.point_names];
    const rows: string[] = [headers.join(',')];

    // Prepare data rows - align by timestamp
    const maxLength = Math.max(...plot_data.timestamps.map(t => t.length));
    for (let i = 0; i < maxLength; i++) {
      const row: string[] = [];
      // Use the first timestamp array as reference
      if (plot_data.timestamps[0] && plot_data.timestamps[0][i]) {
        row.push(plot_data.timestamps[0][i]);
      } else {
        row.push('');
      }
      // Add values for each series
      for (let j = 0; j < plot_data.values.length; j++) {
        row.push(plot_data.values[j][i]?.toString() || '');
      }
      rows.push(row.join(','));
    }

    const csvContent = rows.join('\n');
    navigator.clipboard.writeText(csvContent);
  };

  const handleDownloadCSV = () => {
    // Convert plot data to CSV string
    const headers = ['timestamp', ...plot_data.point_names];
    const rows: string[] = [headers.join(',')];

    // Prepare data rows - align by timestamp
    const maxLength = Math.max(...plot_data.timestamps.map(t => t.length));
    for (let i = 0; i < maxLength; i++) {
      const row: string[] = [];
      // Use the first timestamp array as reference
      if (plot_data.timestamps[0] && plot_data.timestamps[0][i]) {
        row.push(plot_data.timestamps[0][i]);
      } else {
        row.push('');
      }
      // Add values for each series
      for (let j = 0; j < plot_data.values.length; j++) {
        row.push(plot_data.values[j][i]?.toString() || '');
      }
      rows.push(row.join(','));
    }

    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const date = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `${plot_data.measured_value}_${date}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const plotContent = (
    <>
      {/* Settings Panel */}
      <Box
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${colors.neutral[200]}`,
          backgroundColor: colors.neutral[50],
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: colors.text.primary,
          }}
        >
          Настройки графика
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <FormControl sx={{ minWidth: 240 }} size="small">
            <InputLabel>Метод сглаживания</InputLabel>
            <Select
              value={smoothingMethod}
              label="Метод сглаживания"
              onChange={(event) => setSmoothingMethod(event.target.value)}
            >
              <MenuItem value="None">Не применять</MenuItem>
              <MenuItem value="Moving Average">Бегущее среднее</MenuItem>
              <MenuItem value="Gaussian">Гауссово</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flex: '1 1 300px', minWidth: 240 }}>
            <Typography
              variant="caption"
              sx={{
                mb: 1,
                color: colors.text.secondary,
                fontWeight: 500,
                display: 'block',
              }}
            >
              Сила сглаживания: {smoothness}
            </Typography>
            <Slider
              min={1}
              max={100}
              step={1}
              value={smoothness}
              onChange={(event, value) => setSmoothness(value as number)}
              valueLabelDisplay="auto"
              disabled={smoothingMethod === 'None'}
              sx={{
                color: colors.primary.main,
                '& .MuiSlider-thumb': {
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: `0 0 0 8px rgba(8, 131, 149, 0.16)`,
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Controls Toolbar */}
      <Box sx={{ mb: 2 }}>
        <PlotControlsToolbar
          onFullscreen={handleFullscreen}
          onResetZoom={handleResetZoom}
          onToggleGrid={handleToggleGrid}
          onToggleLegend={handleToggleLegend}
          onCopyData={handleCopyData}
          onDownloadCSV={handleDownloadCSV}
          gridVisible={gridVisible}
          legendVisible={legendVisible}
        />
      </Box>

      {/* Plot */}
      <Box
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${colors.neutral[200]}`,
        }}
      >
        <Plot
          data={data_for_plot}
          useResizeHandler={true}
          style={{ width: '100%', height: 600 }}
          layout={{
            ...layout,
            yaxis: {
              ...layout.yaxis,
              title: plot_data.measured_value,
              showgrid: gridVisible,
            },
            xaxis: {
              ...layout.xaxis,
              showgrid: gridVisible,
            },
            showlegend: legendVisible,
          }}
          config={plotlyConfig}
          onRelayout={handleRelayout}
        />
      </Box>
    </>
  );

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 3,
          pb: 2,
          borderBottom: `2px solid ${colors.neutral[200]}`,
        }}
      >
        <BubbleChartIcon sx={{ color: colors.primary.main, fontSize: '1.5rem' }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.text.primary,
          }}
        >
          {plot_data.measured_value}
        </Typography>
      </Box>

      {plotContent}

      {/* Fullscreen Modal */}
      <PlotFullscreenModal
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        title={plot_data.measured_value}
      >
        {plotContent}
      </PlotFullscreenModal>
    </Box>
  );
};